"use client";

import { getWithAuth, postWithAuth } from "@/lib/api-helpers";
import { getBrowserTimeZone, isValidTimeZone, setAmbientTimeZone } from "@/lib/timezone";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * The user's display timezone.
 *
 * Two settings, matching what the Preferences panel shows:
 *   autoTimeZone — follow this device. On by default, so a traveller just sees
 *                  local time without touching anything.
 *   timeZone     — an explicit IANA pick, used only when auto is off.
 *
 * Notion stores the same pair but its value never reaches the screen — it is a
 * server-side signal for reminder emails. Ours drives rendering too, otherwise
 * the picker saves a choice and visibly does nothing.
 *
 * SSR: the server's zone is not the user's, so `timeZone` is `undefined` until
 * the client mounts. Formatters treat undefined as "use the runtime default",
 * which is exactly today's behaviour — so the first paint is unchanged and
 * nothing hydration-mismatches.
 *
 * BACKEND CONTRACT — not implemented yet, so this degrades rather than breaks:
 * until the endpoint exists every call fails, `isUnavailable` goes true, and
 * rendering falls back to this device. Modelled on the one working user-scoped
 * pair, /api/user/notification-preferences.
 *
 *   GET  /api/user/preferences
 *     → { preferences: { timeZone: string | null, autoTimeZone: boolean } }
 *
 *   POST /api/user/preferences
 *     ← { preferences: { timeZone?: string | null, autoTimeZone?: boolean } }
 *     → the full merged preferences object
 *
 * The patch is partial and merged server-side. `timeZone` must be validated as
 * an IANA identifier and rejected otherwise — the client rolls back on failure.
 * Keep the resource general rather than timezone-specific: "start week on
 * Monday" is already stubbed in the same Preferences panel and belongs here.
 */

const ENDPOINT = "/api/user/preferences";

export interface UserTimeZoneState {
  /** Zone to render in. `undefined` before hydration — pass it straight to Intl. */
  timeZone: string | undefined;
  /** The stored manual pick, independent of whether auto is on. */
  storedTimeZone: string | null;
  autoTimeZone: boolean;
  isLoading: boolean;
  isSaving: boolean;
  /** True when the preference could not be loaded; rendering falls back to this device. */
  isUnavailable: boolean;
  setTimeZone: (zone: string) => Promise<void>;
  setAutoTimeZone: (auto: boolean) => Promise<void>;
}

const UserTimeZoneContext = createContext<UserTimeZoneState | null>(null);

export function UserTimeZoneProvider({ children }: { children: React.ReactNode }) {
  const [browserZone, setBrowserZone] = useState<string | undefined>(undefined);
  const [storedTimeZone, setStoredTimeZone] = useState<string | null>(null);
  const [autoTimeZone, setAuto] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUnavailable, setIsUnavailable] = useState(false);

  // Client-only: on the server this would be the server's zone.
  useEffect(() => {
    setBrowserZone(getBrowserTimeZone());
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = (await getWithAuth(ENDPOINT)) as {
          preferences?: { timeZone?: string | null; autoTimeZone?: boolean };
        };
        if (cancelled) return;
        const prefs = res?.preferences;
        if (prefs) {
          setStoredTimeZone(prefs.timeZone && isValidTimeZone(prefs.timeZone) ? prefs.timeZone : null);
          setAuto(prefs.autoTimeZone !== false);
        } else {
          setIsUnavailable(true);
        }
      } catch {
        // The endpoint may not exist yet. Rendering still works — it falls back
        // to this device — so this is a degraded state, not a broken one.
        if (!cancelled) setIsUnavailable(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const persist = useCallback(
    async (patch: { timeZone?: string | null; autoTimeZone?: boolean }, rollback: () => void) => {
      setIsSaving(true);
      try {
        await postWithAuth(ENDPOINT, { preferences: patch });
        setIsUnavailable(false);
      } catch {
        rollback();
        setIsUnavailable(true);
        throw new Error("Could not save your timezone");
      } finally {
        setIsSaving(false);
      }
    },
    [],
  );

  const setTimeZone = useCallback(
    async (zone: string) => {
      if (!isValidTimeZone(zone)) return;
      const previousZone = storedTimeZone;
      const previousAuto = autoTimeZone;
      // Picking a zone by hand means you want that zone, not this device's.
      setStoredTimeZone(zone);
      setAuto(false);
      await persist({ timeZone: zone, autoTimeZone: false }, () => {
        setStoredTimeZone(previousZone);
        setAuto(previousAuto);
      });
    },
    [storedTimeZone, autoTimeZone, persist],
  );

  const setAutoTimeZone = useCallback(
    async (auto: boolean) => {
      const previousAuto = autoTimeZone;
      const previousZone = storedTimeZone;
      setAuto(auto);
      // Turning auto off with nothing picked yet: keep this device's zone, so
      // the switch changes nothing on screen until the user chooses.
      const zone = auto ? storedTimeZone : (storedTimeZone ?? browserZone ?? null);
      if (!auto) setStoredTimeZone(zone);
      await persist({ autoTimeZone: auto, timeZone: zone }, () => {
        setAuto(previousAuto);
        setStoredTimeZone(previousZone);
      });
    },
    [autoTimeZone, storedTimeZone, browserZone, persist],
  );

  const resolvedZone = autoTimeZone ? browserZone : (storedTimeZone ?? browserZone ?? undefined);

  // Publish to the ambient slot so plain modules — IndexedDB eviction, meeting
  // titles, system time columns — can render in the viewer's zone without a hook.
  //
  // Deliberately during RENDER, not in an effect. Readers like getCellValue run
  // while rendering, and a module variable set afterwards re-renders nothing —
  // so an effect left every one of them a render behind, showing the device's
  // zone indefinitely. A parent renders before its children, so setting it here
  // means the whole tree below sees the current value. The write is idempotent
  // and derived from the same state as the context, so a double render under
  // StrictMode is harmless.
  setAmbientTimeZone(resolvedZone);

  const value = useMemo<UserTimeZoneState>(() => {
    const resolved = resolvedZone;
    return {
      timeZone: resolved,
      storedTimeZone,
      autoTimeZone,
      isLoading,
      isSaving,
      isUnavailable,
      setTimeZone,
      setAutoTimeZone,
    };
  }, [resolvedZone, storedTimeZone, autoTimeZone, isLoading, isSaving, isUnavailable, setTimeZone, setAutoTimeZone]);

  return <UserTimeZoneContext.Provider value={value}>{children}</UserTimeZoneContext.Provider>;
}

/**
 * The zone to render instants in. Safe outside the provider — it falls back to
 * this device, which is what every call site did before the preference existed.
 */
export function useUserTimeZone(): UserTimeZoneState {
  const ctx = useContext(UserTimeZoneContext);
  const [fallback, setFallback] = useState<string | undefined>(undefined);
  useEffect(() => {
    if (!ctx) setFallback(getBrowserTimeZone());
  }, [ctx]);

  if (ctx) return ctx;
  return {
    timeZone: fallback,
    storedTimeZone: null,
    autoTimeZone: true,
    isLoading: false,
    isSaving: false,
    isUnavailable: true,
    setTimeZone: async () => undefined,
    setAutoTimeZone: async () => undefined,
  };
}
